/**
 * i18n — app UI localization (en/it/es/fr).
 *
 * Language is auto-detected from the browser (with a manual switcher) and cached
 * in localStorage. English is the fallback. User-authored puzzle CONTENT is never
 * translated — only the app's own chrome.
 *
 * Theme microcopy: English lives in THEMES[theme].microcopy (App.jsx). The
 * locale files override it under themes.<theme>.<key>; useThemeMicrocopy() merges
 * English base + locale overrides so any untranslated key falls back cleanly.
 */
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./locales/en.json";
import it from "./locales/it.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";

export const SUPPORTED_LANGS = ["en", "it", "es", "fr"];
export const LANG_LABELS = { en: "English", it: "Italiano", es: "Español", fr: "Français" };

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      it: { translation: it },
      es: { translation: es },
      fr: { translation: fr },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGS,
    nonExplicitSupportedLngs: true, // it-IT -> it
    interpolation: { escapeValue: false },
    react: { useSuspense: false }, // inline resources load sync, so no Suspense boundary is needed
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "puzzly_lang",
      caches: ["localStorage"],
    },
  });

/**
 * Resolve a theme's microcopy for the active language: English base overlaid
 * with locale overrides. Untranslated keys fall back to the English base.
 * @param {string} theme
 * @param {object} englishBase  THEMES[theme].microcopy
 */
export function useThemeMicrocopy(theme, englishBase = {}) {
  const { i18n: inst } = useTranslation();
  const lang = inst.resolvedLanguage || inst.language || "en";
  if (lang === "en") return englishBase || {};
  const bundle = inst.getResourceBundle(lang, "translation");
  const overrides = (bundle && bundle.themes && bundle.themes[theme]) || {};
  return { ...(englishBase || {}), ...overrides };
}

export default i18n;
